import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * Colores de los tokens de Novarium en el valor literal que pide ApexCharts. Los tokens viven en `.nv-scope`, no en
 * `:root`, así que se leen desde un elemento de la vista.
 * TODO(design-system): gráficos. Novarium no define colores de series ni de escala de calor; se arman solo con tokens.
 */
export interface ChartTokens {
    font: string;
    textStrong: string;
    text2: string;
    divider: string;
    card: string;
    primary: string;
    teal300: string;
    teal700: string;
    teal800: string;
    prio: string;
    warn: string;
    /** Una serie por color (hasta 5): la forma del marcador y el nombre en la leyenda y el tooltip la distinguen. */
    series: string[];
}

function token(style: CSSStyleDeclaration, name: string): string {
    return style.getPropertyValue(name).trim();
}

export function readChartTokens(element: HTMLElement): ChartTokens {
    const style = getComputedStyle(element);
    const primary = token(style, '--primary');
    const teal700 = token(style, '--teal-700');
    const prio = token(style, '--prio');
    const warn = token(style, '--warn');
    const text2 = token(style, '--text-2');
    return {
        font: token(style, '--font'),
        textStrong: token(style, '--text-strong'),
        text2,
        divider: token(style, '--divider'),
        card: token(style, '--card'),
        primary,
        teal300: token(style, '--teal-300'),
        teal700,
        teal800: token(style, '--teal-800'),
        prio,
        warn,
        series: [primary, teal700, prio, warn, text2],
    };
}

/** Los tokens leídos desde `ref`; null hasta que el elemento está montado. */
export function useChartTokens(ref: RefObject<HTMLElement>): ChartTokens | null {
    const [tokens, setTokens] = useState<ChartTokens | null>(null);
    useLayoutEffect(() => {
        if (ref.current) setTokens(readChartTokens(ref.current));
    }, [ref]);
    return tokens;
}

function hexToRgb(hex: string): [number, number, number] {
    const clean = hex.replace('#', '');
    return [0, 2, 4].map(start => parseInt(clean.substring(start, start + 2), 16)) as [number, number, number];
}

/** Color intermedio entre dos hex (`amount` de 0 a 1), para la escala del mapa de calor. */
export function mixHex(from: string, to: string, amount: number): string {
    const a = hexToRgb(from);
    const b = hexToRgb(to);
    const channel = (index: number) => Math.round(a[index] + (b[index] - a[index]) * amount)
        .toString(16).padStart(2, '0');
    return `#${channel(0)}${channel(1)}${channel(2)}`;
}
